import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BusinessUnit } from '@/types';
import {
  businessUnitsApi,
  type BusinessUnitCreateInput,
  type BusinessUnitUpdateInput,
  type RegionListParams,
} from '@/api/business-units';

export const businessUnitKeys = {
  all: ['business-units'] as const,
  listByBrand: (brandId: number) => [...businessUnitKeys.all, 'list', brandId] as const,
  detail: (id: number) => [...businessUnitKeys.all, 'detail', id] as const,
  regions: (params: object = {}) => ['regions', params] as const,
};

/** Business units are only listable per-brand on the backend. */
export function useBusinessUnits(brandId: number | undefined) {
  return useQuery({
    queryKey: businessUnitKeys.listByBrand(brandId ?? 0),
    queryFn: () => businessUnitsApi.listByBrand(brandId as number),
    enabled: brandId !== undefined && brandId > 0,
  });
}

/**
 * The backend has no "list all business units" endpoint — business units are
 * scoped to a brand. This fans out one query per brand id (e.g. for the
 * global filter bar, which needs business units across several brands) and
 * merges the results.
 */
export function useBusinessUnitsForBrands(brandIds: number[]) {
  const results = useQueries({
    queries: brandIds.map((brandId) => ({
      queryKey: businessUnitKeys.listByBrand(brandId),
      queryFn: () => businessUnitsApi.listByBrand(brandId),
    })),
  });

  const data: BusinessUnit[] = results.flatMap((r) => r.data ?? []);
  const isLoading = results.some((r) => r.isLoading);

  return { data, isLoading };
}

export function useRegions(params: RegionListParams = {}) {
  return useQuery({
    queryKey: businessUnitKeys.regions(params),
    queryFn: () => businessUnitsApi.listRegions(params),
    staleTime: 1000 * 60 * 60,
  });
}

export function useCreateBusinessUnit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BusinessUnitCreateInput) => businessUnitsApi.create(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: businessUnitKeys.all }),
  });
}

export function useUpdateBusinessUnit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: BusinessUnitUpdateInput }) =>
      businessUnitsApi.update(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: businessUnitKeys.all }),
  });
}

export function useDeleteBusinessUnit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => businessUnitsApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: businessUnitKeys.all }),
  });
}
